import { useState } from 'react';
import { SEO } from '../../utils/seo';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

type PortfolioType = 'land' | 'infrastructure' | 'mixed';
type Frequency = 'annual' | 'semi-annual' | 'quarterly';
type Depth = 'standard' | 'enhanced' | 'full';

export default function PortfolioMonitoringPricingPage() {
  const [portfolioType, setPortfolioType] = useState<PortfolioType>('land');
  const [landAcres, setLandAcres] = useState(2500);
  const [assetCount, setAssetCount] = useState(50);
  const [frequency, setFrequency] = useState<Frequency>('annual');
  const [depth, setDepth] = useState<Depth>('standard');

  const scrollToEstimator = () => {
    document.getElementById('estimator')?.scrollIntoView({ behavior: 'smooth' });
  };

  const calculateEstimate = () => {
    let baseMin = 0;
    let baseMax = 0;
    let multiplier = 1.0;

    if (portfolioType === 'land') {
      const acres = landAcres;
      baseMin = acres * 3;
      baseMax = acres * 6;
      
      if (acres < 2500) {
        multiplier = 1.5 - ((acres - 100) * (0.5 / 2400));
      }
    } else if (portfolioType === 'infrastructure') {
      const assets = assetCount;
      baseMin = assets * 500;
      baseMax = assets * 1500;
      
      if (assets < 50) {
        multiplier = 1.5 - ((assets - 5) * (0.5 / 45));
      }
    } else {
      const acres = landAcres;
      const assets = assetCount;
      
      const landMin = acres * 3;
      const landMax = acres * 6;
      const infraMin = assets * 500;
      const infraMax = assets * 1500;
      
      baseMin = landMin + infraMin;
      baseMax = landMax + infraMax;
      
      const landMult = acres < 2500 ? 1.5 - ((acres - 100) * (0.5 / 2400)) : 1.0;
      const infraMult = assets < 50 ? 1.5 - ((assets - 5) * (0.5 / 45)) : 1.0;
      multiplier = (landMult + infraMult) / 2;
    }

    const freqMultiplier = frequency === 'annual' ? 1.0 : frequency === 'semi-annual' ? 1.7 : 3.0;
    const depthMultiplier = depth === 'standard' ? 1.0 : depth === 'enhanced' ? 1.25 : 1.5;

    const finalMin = Math.round(baseMin * multiplier * freqMultiplier * depthMultiplier);
    const finalMax = Math.round(baseMax * multiplier * freqMultiplier * depthMultiplier);

    return { min: finalMin, max: finalMax };
  };

  const estimate = calculateEstimate();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const faqData = [
    {
      question: 'Why do retainers cost less over time?',
      answer: 'Retainer-based monitoring eliminates mobilization costs for each visit. We establish baselines once, then track changes efficiently. The more frequently we monitor, the faster each inspection becomes because we know exactly what to look for.'
    },
    {
      question: 'What happens when issues are flagged?',
      answer: 'When we detect changes or potential issues, you receive immediate notification with documentation and imagery. You decide how to respond. We can provide follow-up inspections or detailed analysis as needed, but the core monitoring continues on schedule.'
    },
    {
      question: 'How is reporting delivered?',
      answer: 'After each scheduled inspection, you receive a change detection report highlighting any new findings compared to the previous visit. All historical data remains accessible in a centralized dashboard, creating a complete timeline of your portfolio.'
    },
    {
      question: 'How are contracts structured?',
      answer: 'Monitoring retainers are typically annual agreements with quarterly or monthly billing. You can adjust inspection frequency or portfolio scope at renewal. There are no long-term commitments beyond the initial contract period.'
    }
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEO
        title="Portfolio Monitoring Pricing | Predictable Asset Oversight"
        description="Estimate pricing for ongoing drone monitoring of land and infrastructure portfolios. Retainer-based, scalable, and predictable."
        keywords="portfolio monitoring pricing, asset monitoring cost, drone inspection retainer, ongoing monitoring services"
        canonical="https://yourdomain.com/portfolio-monitoring-pricing"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Predictable Pricing for Ongoing Asset Monitoring
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Retainer pricing replaces surprise costs with scheduled visibility and documented oversight.
              </p>
              <button
                onClick={scrollToEstimator}
                className="px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Estimate Your Retainer
              </button>
            </div>
          </div>
        </section>

        {/* Retainer Estimator */}
        <section id="estimator" className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Annual Retainer Estimator
              </h2>
              <p className="text-lg text-slate-600">
                Calculate your estimated annual investment based on portfolio size and monitoring needs
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-8 border border-slate-200">
              {/* Portfolio Type */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-900 mb-3">
                  Portfolio Type
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { value: 'land', label: 'Land & Timber' },
                    { value: 'infrastructure', label: 'Infrastructure & Utilities' },
                    { value: 'mixed', label: 'Mixed Portfolio' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setPortfolioType(option.value as PortfolioType)}
                      className={`px-6 py-4 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                        portfolioType === option.value
                          ? 'bg-teal-600 text-white'
                          : 'bg-white text-slate-700 border border-slate-300 hover:border-teal-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Portfolio Size */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-900 mb-3">
                  Portfolio Size
                </label>
                
                {(portfolioType === 'land' || portfolioType === 'mixed') && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600">Total Acres</span>
                      <input
                        type="number"
                        value={landAcres}
                        onChange={(e) => setLandAcres(Math.max(100, Math.min(100000, parseInt(e.target.value) || 100)))}
                        className="w-32 px-4 py-2 border border-slate-300 rounded-lg text-sm text-right"
                      />
                    </div>
                    <input
                      type="range"
                      min="100"
                      max="100000"
                      step="100"
                      value={landAcres}
                      onChange={(e) => setLandAcres(parseInt(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>100</span>
                      <span>100,000</span>
                    </div>
                  </div>
                )}

                {(portfolioType === 'infrastructure' || portfolioType === 'mixed') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600">Number of Assets</span>
                      <input
                        type="number"
                        value={assetCount}
                        onChange={(e) => setAssetCount(Math.max(5, Math.min(500, parseInt(e.target.value) || 5)))}
                        className="w-32 px-4 py-2 border border-slate-300 rounded-lg text-sm text-right"
                      />
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="500"
                      step="5"
                      value={assetCount}
                      onChange={(e) => setAssetCount(parseInt(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>5</span>
                      <span>500</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Inspection Frequency */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-900 mb-3">
                  Inspection Frequency
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { value: 'annual', label: 'Annual' },
                    { value: 'semi-annual', label: 'Semi-Annual' },
                    { value: 'quarterly', label: 'Quarterly' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setFrequency(option.value as Frequency)}
                      className={`px-6 py-4 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                        frequency === option.value
                          ? 'bg-teal-600 text-white'
                          : 'bg-white text-slate-700 border border-slate-300 hover:border-teal-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Monitoring Depth */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-900 mb-3">
                  Monitoring Depth
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { value: 'standard', label: 'Standard Monitoring', modifier: '' },
                    { value: 'enhanced', label: 'Enhanced Monitoring', modifier: '+25%' },
                    { value: 'full', label: 'Full Oversight Package', modifier: '+50%' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setDepth(option.value as Depth)}
                      className={`px-6 py-4 rounded-lg font-medium transition-all whitespace-nowrap cursor-pointer ${
                        depth === option.value
                          ? 'bg-teal-600 text-white'
                          : 'bg-white text-slate-700 border border-slate-300 hover:border-teal-600'
                      }`}
                    >
                      <div>{option.label}</div>
                      {option.modifier && (
                        <div className="text-sm mt-1 opacity-80">{option.modifier}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="bg-white rounded-lg p-8 border-2 border-teal-600">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">
                  Estimated Annual Investment
                </h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center py-3 border-b border-slate-200">
                    <span className="text-slate-600">Portfolio Summary</span>
                    <span className="font-semibold text-slate-900">
                      {portfolioType === 'land' && `${landAcres.toLocaleString()} acres`}
                      {portfolioType === 'infrastructure' && `${assetCount} assets`}
                      {portfolioType === 'mixed' && `${landAcres.toLocaleString()} acres + ${assetCount} assets`}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-slate-200">
                    <span className="text-slate-600">Inspection Frequency</span>
                    <span className="font-semibold text-slate-900 capitalize">
                      {frequency.replace('-', ' ')}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-slate-200">
                    <span className="text-slate-600">Monitoring Depth</span>
                    <span className="font-semibold text-slate-900 capitalize">
                      {depth === 'standard' ? 'Standard Monitoring' : depth === 'enhanced' ? 'Enhanced Monitoring' : 'Full Oversight Package'}
                    </span>
                  </div>
                </div>

                <div className="bg-teal-50 rounded-lg p-6 mb-6">
                  <div className="text-center">
                    <div className="text-sm text-teal-700 font-semibold mb-2">
                      Estimated Annual Retainer Range
                    </div>
                    <div className="text-4xl font-bold text-teal-900">
                      {formatCurrency(estimate.min)} – {formatCurrency(estimate.max)}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-slate-600 mb-6">
                  Final pricing depends on access, geography, and asset mix. This estimate provides a baseline for planning purposes.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="/instant-quote"
                    className="flex-1 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    Request a Custom Proposal
                  </a>
                  <a
                    href="/instant-quote"
                    className="flex-1 px-6 py-3 bg-white text-teal-600 font-semibold rounded-lg border-2 border-teal-600 hover:bg-teal-50 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    Schedule a Call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <span className="font-semibold text-slate-900 pr-4">
                      {faq.question}
                    </span>
                    <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-xl text-slate-400 flex-shrink-0`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-slate-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
